<template>
  <div id="app">
    <!-- If user is authenticated, show the main app -->
    <template v-if="isAuthenticated && user">
      <GlobalHeader :user="user" :sign-out="handleCognitoSignOut" />
      <div class="main-container">
        <GlobalSidebar />
        <div class="content-area">
          <div v-if="appLoadError && !currentAppComponent" class="error-placeholder">
            <p>Error preparing application component definition:</p>
            <pre>{{ appLoadError.message }}</pre>
            <pre v-if="appLoadError.stack">{{ appLoadError.stack }}</pre>
          </div>
          <component :is="currentAppComponent" v-if="currentAppComponent" :key="appStore.currentAppId" />
          <div v-else-if="!appLoadError" class="loading-placeholder">
            <p>Select an application from the sidebar.</p>
          </div>
        </div>
      </div>
    </template>
    <!-- If not authenticated and not in an auth redirect process, show a login button -->
    <template v-else-if="!authInProgress">
      <div class="login-prompt-container">
        <div class="login-left-column">
          <div class="login-content">
            <div class="logo-container">
              <img :src="rstLogoSrc" alt="RST Logo" class="login-logo" />
              <span class="logo-text">Bridge</span>
            </div>
            <button @click="redirectToCognitoHostedUI" class="login-button">
              Proceed to secure sign in
            </button>
          </div>
        </div>
        <div class="login-right-column">
          <img :src="archGraphicSrc" alt="Arch Graphic" class="arch-graphic" />
        </div>
      </div>
    </template>
    <!-- Show a loading indicator during auth checks -->
    <div v-if="authInProgress" class="loading-placeholder">
      <p>Loading authentication status...</p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, onErrorCaptured, watch } from 'vue';
import { Auth, Hub } from 'aws-amplify';
import type { HubCapsule } from '@aws-amplify/core'; // For Hub payload type
import GlobalHeader from './components/GlobalHeader.vue';
import GlobalSidebar from './components/GlobalSidebar.vue';
import { useAppStore } from './stores/appStore';
import rstLogoSrc from '@/assets/customer_logo.webp'; // <-- IMPORT THE LOGO
import archGraphicSrc from '@/assets/grey_logo.webp'; // <-- IMPORT THE ARCH GRAPHIC
// Default Amplify UI styles are not needed here if not using Authenticator component directly
// import '@aws-amplify/ui-vue/styles.css'; 

// Helper to determine if we're in "local only" mode (dev server AND no Cognito pool ID)
const IS_LOCAL_ONLY_MODE = import.meta.env.DEV && !import.meta.env.VITE_COGNITO_USER_POOL_ID;

const appStore = useAppStore();
const currentAppComponent = computed(() => appStore.currentAppComponent);
const appLoadError = ref<Error | null>(null);

const isAuthenticated = ref(false);
const user = ref<any | null>(null); // To store user attributes
const authInProgress = ref(true); // To handle initial auth check and Hub events

const redirectToCognitoHostedUI = () => {
  if (IS_LOCAL_ONLY_MODE) {
    console.log('[App.vue] In LOCAL_ONLY_MODE, Cognito redirect is skipped.');
    // Optionally, you could simulate an error or do nothing
    // To test the login UI again if needed in local_only_mode, you could set:
    // isAuthenticated.value = false;
    // user.value = null;
    // authInProgress.value = false; 
    return;
  }
  authInProgress.value = true; // Show loading indicator during redirect
  Auth.federatedSignIn(); // This redirects to the Cognito Hosted UI
};

const handleCognitoSignOut = async () => {
  if (IS_LOCAL_ONLY_MODE) {
    console.log('[App.vue] In LOCAL_ONLY_MODE, simulating sign out.');
    isAuthenticated.value = false;
    user.value = null;
    authInProgress.value = false;
    return;
  }
  authInProgress.value = true; // Show loading indicator
  try {
    await Auth.signOut();
    // isAuthenticated and user will be updated by the Hub listener
  } catch (error) {
    console.error('Error signing out with Cognito: ', error);
    authInProgress.value = false; // Hide loading if sign out fails
  }
};

onMounted(() => {
  if (IS_LOCAL_ONLY_MODE) {
    console.log('[App.vue] In LOCAL_ONLY_MODE, bypassing Amplify auth and mocking user.');
    isAuthenticated.value = true;
    user.value = {
      username: 'local-dev-user',
      attributes: {
        name: 'Local Dev User',
        email: 'dev@example.com',
        // Add any other attributes your GlobalHeader or app might expect
      },
    };
    authInProgress.value = false;
    // DO NOT set up Hub listener or call currentAuthenticatedUser in this mode
    return; // IMPORTANT: Skip the rest of onMounted for local_only
  }

  // --- Full Cognito Auth Logic (for dev-with-cognito or production) ---
  // Listener for authentication events from Amplify Hub
  const hubListenerCancel = Hub.listen('auth', ({ payload }: HubCapsule) => {
    const { event, data } = payload;
    switch (event) {
      case 'signIn': // This event fires after successful sign in via Hosted UI
      case 'cognitoHostedUI':
        console.log('User signed in via Hosted UI:', data);
        isAuthenticated.value = true;
        user.value = data; // data here is the CognitoUser object
        authInProgress.value = false;
        // Potentially redirect to a specific page if on a callback route
        // if (window.location.pathname === '/callback') { router.push('/'); }
        break;
      case 'signOut':
        console.log('User signed out');
        isAuthenticated.value = false;
        user.value = null;
        authInProgress.value = false;
        break;
      case 'signIn_failure':
      case 'cognitoHostedUI_failure':
        console.error('Sign in failure with Hosted UI', data);
        isAuthenticated.value = false;
        user.value = null;
        authInProgress.value = false;
        // Handle the error, maybe show a message to the user
        break;
      default:
        // console.log('Hub Auth event:', event, data);
        break;
    }
  });

  // Check current authenticated user on page load
  Auth.currentAuthenticatedUser()
    .then(currentUser => {
      console.log('User currently authenticated:', currentUser);
      isAuthenticated.value = true;
      user.value = currentUser; // currentUser is the CognitoUser object
      authInProgress.value = false;
    })
    .catch(err => {
      console.log('No user currently authenticated:', err);
      isAuthenticated.value = false;
      user.value = null;
      authInProgress.value = false;
    });
  
  // Clean up the Hub listener when the component is unmounted
  onUnmounted(() => {
    // Conditionally unsubscribe only if the listener was set up
    if (!IS_LOCAL_ONLY_MODE && hubListenerCancel) {
       hubListenerCancel(); // Call the unsubscribe function
    }
  });
});

onErrorCaptured((err, _instance, info) => {
  console.error('Runtime error captured in App.vue:', err, info);
  if (!appLoadError.value) {
    appLoadError.value = err;
  }
  return false; // Prevent further propagation if handled
});

watch(() => appStore.currentAppId, (newAppId, oldAppId) => {
  if (newAppId !== oldAppId) {
    appLoadError.value = null; // Clear component load error when app changes
  }
});

</script>

<style>
/* Global styles remain the same */
* {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

:root {
  --global-header-height: 60px;
  --global-sidebar-width: 60px;
  /* Amplify UI theme variables are not strictly needed here if not using its components directly */
}

body {
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  overflow: hidden; /* Keep this if your app layout relies on it */
}

#app {
  width: 100vw;
  height: 100vh;
  margin: 0;
  padding: 0;
  overflow: hidden; /* Keep this */
  display: flex;
  flex-direction: column;
}

/* Styles for the login prompt if not using Amplify Authenticator component */
.login-prompt-container {
  display: flex;
  height: 100vh; /* Full viewport height */
  width: 100vw; /* Full viewport width */
  background-color: #f0f2f5; /* A light background for the page */
  flex-direction: row; /* Arrange columns side-by-side */
}

/* NEW STYLES */
.login-left-column {
  flex: 1; /* Takes up half the space, or adjust as needed e.g., flex-basis: 50% */
  display: flex;
  flex-direction: column;
  justify-content: center; /* Vertically center content */
  align-items: center; /* Horizontally center content in the column */
  padding: 40px;
  background-color: white; /* Assuming left side has a white background */
}

.login-content {
  display: flex;
  flex-direction: column;
  align-items: center; /* Center items like logo and button */
  text-align: center;
  width: 100%;
  max-width: 350px; /* Max width for the content within the left column */
}

.logo-container {
  display: flex;
  align-items: center;
  margin-bottom: 30px; /* Space below logo group */
}

.login-logo {
  height: 50px; /* Adjust as needed */
  margin-right: 10px;
}

.logo-text {
  font-size: 28px; /* Adjust as needed */
  font-weight: bold;
  color: #333;
}

.login-button {
  background-color: #5E6977; /* Similar to header */
  color: white;
  border: none;
  padding: 12px 20px;
  border-radius: 5px;
  font-size: 16px;
  font-weight: bold;
  cursor: pointer;
  transition: background-color 0.3s ease;
  width: 100%; /* Make button full width of its container */
}

.login-button:hover {
  background-color: #4A5460; /* Darker shade */
}

.login-right-column {
  flex: 1; /* Takes up the other half */
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden; /* If arch is very large */
}

.arch-graphic {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain; /* Or 'cover' depending on how you want it to scale */
}

.main-container {
  display: flex;
  flex-grow: 1;
  overflow: hidden;
  height: calc(100vh - var(--global-header-height));
}

.content-area {
  flex-grow: 1;
  overflow: auto;
  padding: 0;
  margin-left: var(--global-sidebar-width);
}

.loading-placeholder {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  font-size: 1.2em;
  color: #777;
}

.error-placeholder {
  padding: 20px;
  color: red;
  background-color: #ffe0e0;
  border: 1px solid red;
  border-radius: 5px;
  height: 100%;
  overflow: auto;
}
.error-placeholder pre {
  white-space: pre-wrap;
  word-break: break-all;
  margin-top: 10px;
  font-size: 0.9em;
}
</style> 